import React, { useEffect, useRef } from 'react';
// Blueprint icon removed — inline SVG
import { useGlobalMetadata } from '../../utils/providers';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { useHoverPrefetch } from '../../hooks/useHoverPrefetch';
import { prefetchRecordDestinationIfAbsent } from '../../utils/recordDestinationPrefetch';
import './RecordLink.css';

interface RecordLinkProps {
  tableName: string;
  recordId: string | number;
  className?: string;
  title?: string;
}

/**
 * Semantic link component for navigating to record detail pages.
 *
 * Merchant records open in the persistent right-side panel. All other records
 * use their normal full-page route.
 *
 * Uses native DOM addEventListener instead of React onClick because React's synthetic
 * event system doesn't work properly inside ag-Grid cell renderers. The grid's DOM
 * manipulation bypasses React's event attachment, so we need to use native events.
 */
const RecordLink: React.FC<RecordLinkProps> = ({
  tableName,
  recordId,
  className = '',
  title,
}) => {
  const { data: metadata } = useGlobalMetadata();
  const { openRecord, buildRecordUrl } = useRecordNavigator();
  const { schedule, cancel } = useHoverPrefetch();
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Get route name from metadata
  const routeName = metadata?.routeData?.tableToRouteName?.[tableName];

  // Canonical target so ctrl/middle-click opens a new tab that actually
  // resolves to this record, rather than a /{route}/{id} path with no route.
  const tableLabel = metadata?.tableData?.byName?.[tableName]?.ui_label || tableName;
  const defaultTitle = title || (tableName === 'merchant'
    ? `Open ${tableLabel} record in panel`
    : `Open ${tableLabel} record`);

  // Use native DOM events instead of React onClick
  useEffect(() => {
    const linkElement = linkRef.current;
    if (!linkElement || !routeName || !recordId) return;

    const handleClick = (e: MouseEvent) => {
      // Preserve native modifier/middle-click browser-tab behavior.
      if (e.ctrlKey || e.metaKey || e.button === 1) return;

      e.preventDefault();
      e.stopPropagation();
      openRecord({
        id: recordId,
        table: routeName,
        tableName,
        label: `${tableLabel} #${recordId}`,
      });
    };

    const handleContextMenu = (e: MouseEvent) => {
      const iconElement = linkElement.querySelector('.record-link-icon');
      if (iconElement && iconElement.contains(e.target as Node)) {
        // Allow native context menu on the icon without ag-Grid intercepting it.
        e.stopPropagation();
      }
    };

    const handleIntent = () => {
      schedule(() => prefetchRecordDestinationIfAbsent({ tableName, id: recordId }, metadata));
    };

    // Attach native event listener
    linkElement.addEventListener('click', handleClick);
    linkElement.addEventListener('contextmenu', handleContextMenu);
    linkElement.addEventListener('pointerenter', handleIntent);
    linkElement.addEventListener('pointerleave', cancel);
    linkElement.addEventListener('focus', handleIntent);
    linkElement.addEventListener('blur', cancel);

    // Cleanup
    return () => {
      linkElement.removeEventListener('click', handleClick);
      linkElement.removeEventListener('contextmenu', handleContextMenu);
      linkElement.removeEventListener('pointerenter', handleIntent);
      linkElement.removeEventListener('pointerleave', cancel);
      linkElement.removeEventListener('focus', handleIntent);
      linkElement.removeEventListener('blur', cancel);
    };
  }, [cancel, metadata, openRecord, recordId, routeName, schedule, tableLabel, tableName]);

  if (!routeName || !recordId) return null;

  const href = buildRecordUrl({ tableName, id: recordId });

  return (
    <a
      ref={linkRef}
      href={href}
      className={`record-link ${className}`}
      aria-label={defaultTitle}
      title={defaultTitle}
    >
      <svg className="record-link-icon" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
};

export default RecordLink;
